import type * as React from 'react';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export const Menu = BaseMenu.Root;

export type MenuTriggerProps = Omit<BaseMenu.Trigger.Props, 'style'>;

/*
 * Unstyled on purpose. A menu is opened by something that already has a look —
 * a Button, an IconButton, a line of text — so the trigger merges into it with
 * Base UI's `render` rather than adding a second appearance of its own.
 */
export function MenuTrigger(props: MenuTriggerProps) {
  return <BaseMenu.Trigger data-slot='menu-trigger' {...props} />;
}

export interface MenuContentProps extends Omit<
  BaseMenu.Popup.Props,
  'className' | 'style'
> {
  className?: string;
  side?: BaseMenu.Positioner.Props['side'];
  align?: BaseMenu.Positioner.Props['align'];
  sideOffset?: number;
  /*
   * Rows edge to edge, for a menu whose current choice is a filled row. An
   * inset, rounded row leaves white wedges around the fill, so the mark reads
   * as a badge stuck on the row rather than as the row being the chosen one.
   * The popup clips its rows to its own corners.
   */
  flush?: boolean;
}

/* Below its trigger and left-aligned, which is where a menu on a line of text belongs. */
export function MenuContent({
  className,
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  flush = false,
  ...props
}: MenuContentProps) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        className='z-50'
      >
        <BaseMenu.Popup
          data-slot='menu-content'
          className={cn(
            'min-w-48 rounded-md border border-line bg-surface shadow-menu outline-none animate-pop',
            flush
              ? 'overflow-hidden [&_[data-slot=menu-item]]:rounded-none [&_[data-slot=menu-item]]:px-3.5 [&_[data-slot=menu-group-label]]:px-3.5 [&_[data-slot=menu-separator]]:mx-0'
              : 'p-1.5',
            className,
          )}
          {...props}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

export const menuItemVariants = cva(
  [
    'flex w-full cursor-pointer items-center gap-2.5 rounded-sm px-2.75 py-2.25 text-left',
    'text-sm font-semibold transition-colors outline-none',
    '[&_svg]:size-3.75 [&_svg]:shrink-0',
  ],
  {
    variants: {
      tone: {
        default: 'text-ink [&_svg]:text-ink-muted hover:bg-fill data-highlighted:bg-fill',
        danger:
          'text-danger [&_svg]:text-danger hover:bg-danger-fill data-highlighted:bg-danger-fill',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  },
);

export type MenuItemVariants = VariantProps<typeof menuItemVariants>;

export interface MenuItemProps
  extends Omit<BaseMenu.Item.Props, 'className' | 'style'>, MenuItemVariants {
  className?: string;
}

export function MenuItem({ className, tone, ...props }: MenuItemProps) {
  return (
    <BaseMenu.Item
      data-slot='menu-item'
      className={cn(menuItemVariants({ tone }), className)}
      {...props}
    />
  );
}

export interface MenuRadioGroupProps extends Omit<
  React.ComponentProps<typeof BaseMenu.RadioGroup>,
  'style' | 'value' | 'onValueChange'
> {
  /** Null while nothing has been chosen yet — no row is ticked. */
  value: string | null;
  onValueChange?: (value: string) => void;
}

/* One of these, and only one: the rows below carry a tick rather than an act. */
export function MenuRadioGroup({ onValueChange, ...props }: MenuRadioGroupProps) {
  return (
    <BaseMenu.RadioGroup
      data-slot='menu-radio-group'
      onValueChange={(next: string) => onValueChange?.(next)}
      {...props}
    />
  );
}

export interface MenuRadioItemProps extends Omit<
  BaseMenu.RadioItem.Props,
  'className' | 'style' | 'value'
> {
  value: string;
  className?: string;
}

/**
 * A row that is either the chosen one or not — a microphone, a language.
 *
 * The tick sits in a slot the unticked rows keep empty, so a list of names
 * still reads as a column rather than shifting by an icon's width the moment
 * one of them is picked. Choosing closes the menu: the question it was opened
 * for has been answered.
 */
export function MenuRadioItem({
  className,
  children,
  closeOnClick = true,
  ...props
}: MenuRadioItemProps) {
  return (
    <BaseMenu.RadioItem
      data-slot='menu-item'
      closeOnClick={closeOnClick}
      className={cn(menuItemVariants(), className)}
      {...props}
    >
      <span className='flex size-3.75 shrink-0 items-center justify-center'>
        <BaseMenu.RadioItemIndicator>
          <Check aria-hidden='true' />
        </BaseMenu.RadioItemIndicator>
      </span>
      {children}
    </BaseMenu.RadioItem>
  );
}

export type MenuGroupProps = Omit<React.ComponentProps<typeof BaseMenu.Group>, 'style'>;

/*
 * Items that belong together, so a label can name them. Unstyled: the grouping
 * is what the label and the separators draw, not a box of its own.
 */
export function MenuGroup(props: MenuGroupProps) {
  return <BaseMenu.Group data-slot='menu-group' {...props} />;
}

export type MenuGroupLabelProps = Omit<
  React.ComponentProps<typeof BaseMenu.GroupLabel>,
  'style'
>;

/*
 * What the items under it are a list of — "Kies een gesprekstype".
 *
 * For a menu whose items are values to pick from rather than acts to perform: a
 * list of names with nothing above them leaves the reader to work out what the
 * names have in common. Base UI's GroupLabel rather than a plain line of text,
 * so a screen reader reads the same grouping.
 */
export function MenuGroupLabel({ className, ...props }: MenuGroupLabelProps) {
  return (
    <BaseMenu.GroupLabel
      data-slot='menu-group-label'
      className={cn(
        'px-2.75 pt-1.5 pb-1 text-xs font-bold tracking-widest text-ink-muted uppercase',
        className,
      )}
      {...props}
    />
  );
}

export type MenuSeparatorProps = Omit<
  React.ComponentProps<typeof BaseMenu.Separator>,
  'style'
>;

export function MenuSeparator({ className, ...props }: MenuSeparatorProps) {
  return (
    <BaseMenu.Separator
      data-slot='menu-separator'
      className={cn('mx-1 my-1.25 h-px bg-fill', className)}
      {...props}
    />
  );
}
